import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Checkbox, Mail01, TextInput } from "@opendate-app/setlist";
import TurnstileChallenge from "../../../shared/TurnstileChallenge";
import TurnstileDisclosure from "../../../shared/TurnstileDisclosure";
import { validateEmail } from "../../../shared/validationUtils";
import useFocusOnChange from "../../ClassesShared/useFocusOnChange";

const CONSENT_ERROR_ID = "pc-next-session-consent-error";

const signupErrors = (email, consent) => {
  const errors = {};
  if (!email.trim()) errors.email = "Enter your email address.";
  else if (!validateEmail(email.trim())) errors.email = "Enter an email address like name@example.com.";
  if (!consent) errors.consent = "Check this box so we can email you.";
  return errors;
};

const resultMessage = (result, email, templateName) => {
  if (!result) return "";
  return result === "signed_up"
    ? `You're on the list. We'll email ${email.trim()} when the next session of ${templateName} is scheduled.`
    : `${email.trim()} is already on the list. We'll email you when the next session of ${templateName} is scheduled.`;
};

export default function ClassNextSessionSignup({ templateName, signUpUrl, turnstileSiteKey, fetchImpl = fetch }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState([]);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const turnstileRef = useRef(null);
  const formRef = useRef(null);
  const statusRef = useFocusOnChange(Boolean(result));
  const fieldErrors = signupErrors(email, consent);
  const fieldError = (field) => (showFieldErrors ? fieldErrors[field] : undefined);

  useEffect(() => {
    if (focusRequest) formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [focusRequest]);

  const verify = async () => {
    if (!turnstileSiteKey || !turnstileRef.current) return null;
    try {
      return await turnstileRef.current.executeAsync();
    } catch (error) {
      turnstileRef.current.reset();
      throw error;
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (working) return;
    if (Object.keys(fieldErrors).length > 0) {
      setShowFieldErrors(true);
      setFocusRequest((count) => count + 1);
      return;
    }
    setWorking(true);
    setErrors([]);
    let recaptchaToken;
    try {
      recaptchaToken = await verify();
    } catch (error) {
      setErrors(["We couldn't verify this browser. Please try again."]);
      setWorking(false);
      return;
    }
    try {
      const response = await fetchImpl(signUpUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ class_template_interest: { email, consent }, recaptcha_token: recaptchaToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrors(data.errors || ["We couldn't add you to the list. Please try again."]);
        return;
      }
      setResult(data.status);
    } catch (error) {
      setErrors(["We couldn't reach the server. Check your connection and try again."]);
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="pc-rail__card pc-next-session" aria-labelledby="pc-next-session-title" data-testid="next-session-signup">
      <div className="pc-rail__section">
        <div className="pc-next-session__header">
          <span className="pc-next-session__icon" aria-hidden="true"><Mail01 width={16} height={16} /></span>
          <h2 id="pc-next-session-title" className="pc-next-session__title">Get notified about the next session</h2>
        </div>
        <p ref={statusRef} tabIndex={-1} className={result ? "pc-next-session__text mb-0" : "sr-only"} role="status" data-testid={result ? `next-session-${result}` : undefined}>
          {resultMessage(result, email, templateName)}
        </p>
        {!result && (
          <form ref={formRef} onSubmit={submit} noValidate>
            <p className="pc-next-session__text">{`We'll email you once when the next session of ${templateName} is scheduled.`}</p>
            {errors.length > 0 && <div className="mb-3"><Alert color="error" role="alert" description={errors.join(" ")} /></div>}
            <div className="pc-next-session__fields">
              <TextInput label="Email" required type="email" autoComplete="email" maxLength={255} placeholder="you@example.com" value={email} error={fieldError("email")} onChange={(e) => setEmail(e.target.value)} />
              <Checkbox
                size="sm"
                label={`Email me about the next session of ${templateName}. I can unsubscribe any time.`}
                checked={consent}
                aria-required="true"
                aria-invalid={fieldError("consent") ? "true" : undefined}
                aria-describedby={fieldError("consent") ? CONSENT_ERROR_ID : undefined}
                onChange={(e) => setConsent(e.target.checked)}
              />
              {fieldError("consent") && <p className="pc-text-error small mt-1 mb-0" id={CONSENT_ERROR_ID}>{fieldError("consent")}</p>}
              {turnstileSiteKey && (
                <div>
                  <TurnstileChallenge ref={turnstileRef} sitekey={turnstileSiteKey} />
                  <TurnstileDisclosure />
                </div>
              )}
              <div className="pc-block-action">
                <Button type="submit" variant="secondary" aria-disabled={working || undefined} data-testid="next-session-submit">
                  {working ? "Signing up…" : "Notify Me"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

ClassNextSessionSignup.propTypes = {
  templateName: PropTypes.string.isRequired,
  signUpUrl: PropTypes.string.isRequired,
  turnstileSiteKey: PropTypes.string,
  fetchImpl: PropTypes.func,
};
